import { useState } from "react";
import { IconButton, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import { LineItem, formatCurrency } from "../helpers/invoiceUtils";
import AddItemDialog from "./AddItemDialog";

interface Props {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
}

export default function LineItemsTable({ items, onChange }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const updateItem = (id: string, field: keyof LineItem, value: any) => {
    onChange(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeItem = (id: string) => {
    onChange(items.filter(item => item.id !== id));
  };

  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 1.5 } };

  return (
    <div>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, py: 1.5, width: 100 }}>Codigo</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, py: 1.5, minWidth: 180 }}>Descripcion</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, py: 1.5, width: 70 }}>Cant.</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, py: 1.5, width: 120 }}>Precio</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, py: 1.5, width: 80 }}>Dto.%</TableCell>
              <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, py: 1.5, width: 90 }}>IVA</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12, py: 1.5, width: 120 }}>Subtotal</TableCell>
              <TableCell sx={{ py: 1.5, width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => {
              const discounted = item.quantity * item.price * (1 - (item.discount_percent || 0) / 100);
              const lineSubtotal = discounted * (1 + item.tax_percent / 100);
              return (
                <TableRow key={item.id} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                  <TableCell sx={{ py: 1.5, color: "text.secondary", fontSize: 13 }}>{item.product_code || "--"}</TableCell>
                  <TableCell sx={{ py: 1.5, fontSize: 13 }}>{item.description}</TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField size="small" type="number" inputProps={{ min: 1 }} value={item.quantity} onChange={(e) => updateItem(item.id, "quantity", Number(e.target.value))} sx={{ width: 65, ...inputSx }} />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField size="small" type="number" inputProps={{ min: 0 }} value={item.price} onChange={(e) => updateItem(item.id, "price", Number(e.target.value))} sx={{ width: 110, ...inputSx }} />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField size="small" type="number" inputProps={{ min: 0, max: 100 }} value={item.discount_percent} onChange={(e) => updateItem(item.id, "discount_percent", Number(e.target.value))} sx={{ width: 70, ...inputSx }} />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <TextField size="small" select value={item.tax_percent} onChange={(e) => updateItem(item.id, "tax_percent", Number(e.target.value))} sx={{ width: 80, ...inputSx }}>
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={19}>19%</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, fontFamily: "monospace", fontSize: 13 }}>{formatCurrency(lineSubtotal)}</TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <IconButton size="small" color="error" onClick={() => removeItem(item.id)}>
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Button startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} sx={{ mt: 2, borderRadius: 2, textTransform: "none" }}>
        Agregar Item
      </Button>
      <AddItemDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onAdd={(item) => onChange([...items, item])} />
    </div>
  );
}
